/**
 * Hooks TanStack Query pour la gestion des abonnements Stripe
 * Architecture 3-couches : Component → Hook (TanStack Query) → Service (subscriptionsAPI)
 *
 * Simplified: Checkout Session + Billing Portal replace direct subscription management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { subscriptionsAPI } from '../utils/api';
import type { TenantSubscription, SubscriptionEvent } from '../types/models';

// ─── Types (camelCase aligné sur Prisma SubscriptionPlan) ─────────────

export interface Plan {
  id: string;
  code: string;
  name: string;
  priceMonthly: number;
  currencyCode: string;
  features: Record<string, boolean> | null;
  isActive: boolean;
  quotas?: Array<{
    metric: string;
    limitValue: number;
  }>;
}

export interface QuotaItem {
  metric: string;
  limit: number;
  used: number;
  remaining: number;
  percentage: number;
}

export interface Quota {
  plan: string;
  planName: string;
  priceMonthly: number;
  features: Record<string, boolean>;
  status: string;
  currentPeriodEnd?: Date;
  trialEndsAt?: Date;
  cancelAtPeriodEnd: boolean;
  quotas: QuotaItem[];
}

export interface StripeConfig {
  publishableKey: string;
  isConfigured: boolean;
  prices?: Record<string, string>;
  annualPrices?: Record<string, string>;
  hasAnnualPricing?: boolean;
}

export interface PlansResponse {
  success: boolean;
  data: Plan[];
  count: number;
}

// ─── Query Keys ───────────────────────────────────────────────────────

export const subscriptionKeys = {
  all: ['subscriptions'] as const,
  config: () => [...subscriptionKeys.all, 'config'] as const,
  plans: () => [...subscriptionKeys.all, 'plans'] as const,
  current: () => [...subscriptionKeys.all, 'current'] as const,
  quotas: () => [...subscriptionKeys.all, 'quotas'] as const,
  checkQuota: (metric: string) => [...subscriptionKeys.all, 'checkQuota', metric] as const,
  history: () => [...subscriptionKeys.all, 'history'] as const,
};

// ─── Query Hooks (lecture) ────────────────────────────────────────────

/** Récupérer la config Stripe (publishableKey + priceId mapping) */
export function useStripeConfig() {
  return useQuery({
    queryKey: subscriptionKeys.config(),
    queryFn: () => subscriptionsAPI.getConfig(),
    staleTime: 10 * 60 * 1000, // 10 minutes — config change rarement
  });
}

/** Récupérer tous les plans disponibles */
export function useSubscriptionPlans() {
  return useQuery({
    queryKey: subscriptionKeys.plans(),
    queryFn: async () => {
      const result = await subscriptionsAPI.getPlans();
      const plans: Plan[] = result.data || [];
      return plans.sort((a, b) => a.priceMonthly - b.priceMonthly);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/** Récupérer l'abonnement actuel du tenant */
export function useCurrentSubscription() {
  return useQuery({
    queryKey: subscriptionKeys.current(),
    queryFn: () => subscriptionsAPI.getCurrent(),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/** Récupérer les quotas et l'utilisation actuelle */
export function useSubscriptionQuotas() {
  return useQuery({
    queryKey: subscriptionKeys.quotas(),
    queryFn: () => subscriptionsAPI.getQuotas(),
    staleTime: 2 * 60 * 1000,
  });
}

/** Vérifier si un quota spécifique est disponible */
export function useCheckQuota(metric: string) {
  return useQuery({
    queryKey: subscriptionKeys.checkQuota(metric),
    queryFn: () => subscriptionsAPI.checkQuota(metric),
    enabled: !!metric,
    staleTime: 60 * 1000, // 1 minute
  });
}

/** Historique des événements d'abonnement */
export function useSubscriptionHistory() {
  return useQuery({
    queryKey: subscriptionKeys.history(),
    queryFn: async () => {
      const result = await subscriptionsAPI.getHistory();
      return result as SubscriptionEvent[];
    },
    staleTime: 2 * 60 * 1000,
  });
}

// ─── Mutation Hooks (écriture) ────────────────────────────────────────

/** Synchroniser le statut depuis Stripe (fallback si webhook manqué) */
export function useSyncSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => subscriptionsAPI.sync(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.current() });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.quotas() });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.history() });
    },
  });
}

/** Créer un Checkout Session et rediriger vers Stripe */
export function useCreateCheckoutSession() {
  return useMutation({
    mutationFn: async ({ planCode, billingCycle = 'monthly' }: { planCode: string; billingCycle?: 'monthly' | 'annual' }) => {
      const { url } = await subscriptionsAPI.createCheckoutSession(
        planCode,
        `${window.location.origin}/checkout?status=success`,
        `${window.location.origin}/checkout?status=cancel`,
        billingCycle
      );
      if (!url || !url.startsWith('https://')) {
        throw new Error('URL de paiement invalide reçue du serveur');
      }
      return url;
    },
    onSuccess: (url) => {
      window.location.href = url;
    },
  });
}

/** Ouvrir le Billing Portal Stripe */
export function useOpenBillingPortal() {
  return useMutation({
    mutationFn: async () => {
      const returnUrl = `${window.location.origin}/billing`;
      const result = await subscriptionsAPI.createBillingPortalSession(returnUrl);
      if (!result?.url || !result.url.startsWith('https://')) {
        throw new Error('URL du portail de facturation invalide');
      }
      return result.url;
    },
    onSuccess: (url) => {
      window.location.href = url;
    },
  });
}

// ─── Legacy hook (rétro-compatibilité temporaire) ─────────────────────
// Certains composants (BillingDashboard) utilisent encore l'ancien pattern.
// Ce wrapper maintient la compatibilité le temps de migrer tous les consommateurs.

export function useSubscriptions() {
  const queryClient = useQueryClient();
  const syncMutation = useSyncSubscription();
  const portalMutation = useOpenBillingPortal();
  const checkoutMutation = useCreateCheckoutSession();

  const getStripeConfig = useCallback(async (): Promise<StripeConfig> => {
    return queryClient.fetchQuery({
      queryKey: subscriptionKeys.config(),
      queryFn: () => subscriptionsAPI.getConfig(),
    });
  }, [queryClient]);

  const getPlans = useCallback(async () => {
    return queryClient.fetchQuery({
      queryKey: subscriptionKeys.plans(),
      queryFn: () => subscriptionsAPI.getPlans(),
    });
  }, [queryClient]);

  const getCurrentSubscription = useCallback(async (): Promise<TenantSubscription | null> => {
    return queryClient.fetchQuery({
      queryKey: subscriptionKeys.current(),
      queryFn: () => subscriptionsAPI.getCurrent(),
    });
  }, [queryClient]);

  const getQuotas = useCallback(async (): Promise<Quota> => {
    return queryClient.fetchQuery({
      queryKey: subscriptionKeys.quotas(),
      queryFn: () => subscriptionsAPI.getQuotas(),
    });
  }, [queryClient]);

  const checkQuota = useCallback(async (type: string): Promise<{ canCreate: boolean; remaining: number }> => {
    return queryClient.fetchQuery({
      queryKey: subscriptionKeys.checkQuota(type),
      queryFn: () => subscriptionsAPI.checkQuota(type),
    });
  }, [queryClient]);

  const createCheckoutSession = useCallback(async (planCode: string, billingCycle: 'monthly' | 'annual' = 'monthly'): Promise<void> => {
    await checkoutMutation.mutateAsync({ planCode, billingCycle });
  }, [checkoutMutation]);

  const getSubscriptionHistory = useCallback(async (): Promise<SubscriptionEvent[]> => {
    return queryClient.fetchQuery({
      queryKey: subscriptionKeys.history(),
      queryFn: () => subscriptionsAPI.getHistory(),
    });
  }, [queryClient]);

  const syncSubscription = useCallback(async () => {
    return syncMutation.mutateAsync();
  }, [syncMutation]);

  const openBillingPortal = useCallback(async (): Promise<void> => {
    await portalMutation.mutateAsync();
  }, [portalMutation]);

  return {
    loading: syncMutation.isPending || portalMutation.isPending || checkoutMutation.isPending,
    error: syncMutation.error?.message || portalMutation.error?.message || checkoutMutation.error?.message || null,
    getStripeConfig,
    getPlans,
    getCurrentSubscription,
    getQuotas,
    checkQuota,
    createCheckoutSession,
    syncSubscription,
    getSubscriptionHistory,
    openBillingPortal,
  };
}
